import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { FullscreenMessageComponent } from '@examdojo/core/fullscreen-message';
import { LoaderComponent } from '@examdojo/core/loader';
import { mapToVoid } from '@examdojo/core/rxjs';
import { PracticeActivityService } from '@examdojo/practice-activity';
import { TranslocoPipe } from '@jsverse/transloco';
import { catchError, combineLatest, defer, map, of, switchMap, take, tap } from 'rxjs';
import { ActivityConflictHandlerService } from '../../../shared/activity-conflict';
import { TopicPracticeDialogService } from '../topic-practice/dialog/topic-practice-dialog.service';

export const START_PRACTICE_REDIRECT_TO_QUERY_PARAM = 'redirectTo';

@Component({
  selector: 'dojo-start-practice',
  template: `
    @if (state.errorMessage) {
      <y42-fullscreen-message
        class="start-practice-not-found"
        [header]="'examdojo.start_practice_error.header' | transloco"
        [description]="state.errorMessage | transloco"
      />
    } @else {
      <div class="flex h-full items-center justify-center">
        <dojo-loader [centered]="true" />
      </div>
    }
  `,
  styles: [
    `
      ::ng-deep .sidenav-content:has(.start-practice-not-found) {
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LoaderComponent, FullscreenMessageComponent, TranslocoPipe],
  host: { class: 'flex flex-col h-full justify-center' },
})
export class StartPracticeComponent {
  constructor(
    private readonly practiceActivityService: PracticeActivityService,
    private readonly activityConflictHandler: ActivityConflictHandlerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
  ) {}

  readonly questionId = input.required<number>();

  readonly errorMessageOrRedirect$ = combineLatest([
    toObservable(this.questionId),
    this.route.queryParams.pipe(
      map((params) => {
        const metadata: Record<string, unknown> = {};
        Object.keys(params).forEach((key) => {
          try {
            metadata[key] = JSON.parse(params[key]);
          } catch {
            metadata[key] = params[key];
          }
        });
        return metadata;
      }),
    ),
  ]).pipe(
    switchMap(([questionId, metadata]) => {
      const redirectTo = metadata['redirectTo'] as string | undefined;
      return defer(() => this.practiceActivityService.startPracticeFromQuestion(questionId, metadata)).pipe(
        this.activityConflictHandler.handleConflict({
          targetActivity: 'practice',
          onCancel: redirectTo ? () => this.router.navigate([redirectTo]) : undefined,
        }),
      );
    }),
    mapToVoid(),
    tap(() => {
      // Open dialog first, then navigate once it's presented
      // This ensures the fullscreen modal covers the sidenav animation
      this.topicPracticeDialogService.openDialog();
    }),
    switchMap(() =>
      this.topicPracticeDialogService.dialogPresented$.pipe(
        take(1),
        tap(() => {
          this.router.navigate(['/practice']);
        }),
      ),
    ),
    catchError(() => of('examdojo.start_practice_error.description')),
  );

  readonly state = connectState({
    errorMessage: this.errorMessageOrRedirect$,
  });
}
